import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import ButtonBase from '@mui/material/ButtonBase';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { formatCurrency, hasValue } from 'utils/formatters';
import { appendOrderStatusParams } from '../../constants';
import PaymentDetailsDialog from './PaymentDetailsDialog';

const STATUS_CONFIG = {
  overdue: { color: '#DC2626' },
  due_next_7_days: { color: '#D97706' },
  received: { color: '#059669' },
};

// Single payment row component
const PaymentRow = ({ label, value, currency, count, subtitle, color, onClick }) => {
  const hasData = hasValue(value);

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        py: 1.5,
        px: 2,
        borderRadius: 1,
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.75rem', mb: 0.5 }}
          >
            {label}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 600,
              color: hasData ? 'text.primary' : 'text.disabled',
            }}
          >
            {hasData ? formatCurrency(value, currency) : '—'}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasData && count > 0 && (
            <Chip
              label={count}
              size="small"
              sx={{
                minWidth: 32,
                height: 24,
                fontWeight: 600,
                fontSize: '0.75rem',
                bgcolor: `${color}14`,
                color: color,
              }}
            />
          )}
          <ChevronRightIcon
            sx={{
              fontSize: 20,
              color: 'text.secondary',
              opacity: 0.6,
            }}
          />
        </Box>
      </Box>
    </ButtonBase>
  );
};

// Currency Lane Component
const CurrencyLane = ({ title, icon, currency, data, onRowClick }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Lane Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: currency === 'USD' ? 'rgba(37, 99, 235, 0.04)' : 'rgba(5, 150, 105, 0.04)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {icon}
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
        <Chip
          label={currency}
          size="small"
          sx={{
            ml: 'auto',
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 22,
            bgcolor: currency === 'USD' ? 'primary.main' : 'success.main',
            color: 'white',
          }}
        />
      </Box>

      {/* Lane Content */}
      <Box sx={{ flex: 1, py: 1 }}>
        <PaymentRow
          label="Overdue"
          value={data.overdue}
          currency={currency}
          count={data.overdueCount}
          subtitle={data.overdueSubtitle}
          color={STATUS_CONFIG.overdue.color}
          onClick={() => onRowClick('Overdue Payments', 'overdue=true')}
        />
        <Divider sx={{ mx: 2 }} />
        <PaymentRow
          label="Due Next 7 Days"
          value={data.dueThisWeek}
          currency={currency}
          count={data.dueThisWeekCount}
          color={STATUS_CONFIG.due_next_7_days.color}
          onClick={() => onRowClick('Due Next 7 Days', 'due_next_7_days=true')}
        />
        <Divider sx={{ mx: 2 }} />
        <PaymentRow
          label="Received This Month"
          value={data.received}
          currency={currency}
          count={data.receivedCount}
          color={STATUS_CONFIG.received.color}
          onClick={() => onRowClick('Received This Month', 'received_this_month=true')}
        />
      </Box>
    </Paper>
  );
};

const PaymentCurrencyLanes = ({ data, selectedStatuses }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({ title: '', filterParams: '' });

  const handleRowClick = (title, filterParams) => {
    setDialogConfig({
      title,
      filterParams: appendOrderStatusParams(filterParams, selectedStatuses),
    });
    setDialogOpen(true);
  };

  const overdue = data?.overdue_summary || {};
  const dueWeek = data?.due_next_7_days || {};
  const received = data?.received_this_month || {};

  const usdData = {
    overdue: overdue.total_usd,
    overdueCount: overdue.payment_count,
    overdueSubtitle: `${overdue.client_count ?? 0} clients`,
    dueThisWeek: dueWeek.total_usd,
    dueThisWeekCount: dueWeek.payment_count,
    received: received.total_usd,
    receivedCount: received.payment_count,
  };

  const brlData = {
    overdue: overdue.total_brl,
    overdueCount: overdue.payment_count,
    overdueSubtitle: `${overdue.client_count ?? 0} clients`,
    dueThisWeek: dueWeek.total_brl,
    dueThisWeekCount: dueWeek.payment_count,
    received: received.total_brl,
    receivedCount: received.payment_count,
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid xs={12} md={6}>
          <CurrencyLane
            title="USD Payments"
            icon={<AttachMoneyIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
            currency="USD"
            data={usdData}
            onRowClick={handleRowClick}
          />
        </Grid>
        <Grid xs={12} md={6}>
          <CurrencyLane
            title="BRL Payments"
            icon={
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'success.main' }}>
                R$
              </Typography>
            }
            currency="BRL"
            data={brlData}
            onRowClick={handleRowClick}
          />
        </Grid>
      </Grid>

      <PaymentDetailsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={dialogConfig.title}
        filterParams={dialogConfig.filterParams}
      />
    </>
  );
};

export default PaymentCurrencyLanes;
